import React, { useState, useEffect } from 'react';
import { Card, Form, Input, InputNumber, Switch, Tabs, Button, Space, message, Spin, Checkbox, Typography } from 'antd';
import { SaveOutlined, ClearOutlined } from '@ant-design/icons';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';
import { CACHE_SCOPES } from '../../utils/cacheScopes';

interface SettingItem {
  key: string;
  value: string | null;
  type: 'string' | 'number' | 'boolean' | 'secret';
  category: string;
  description: string | null;
}

type SettingsGrouped = Record<string, SettingItem[]>;

const categoryLabels: Record<string, string> = {
  oauth: 'OAuth',
  media: 'Media',
  vision: 'Vision',
  general: 'General',
  email: 'Email',
  sms: 'SMS',
  wellness: 'Wellness',
  app: 'App Update',
};

const categoryOrder = ['oauth', 'media', 'vision', 'general', 'email', 'sms', 'wellness', 'app'];

export const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SettingsGrouped>({});
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`${API_BASE_URL}/settings`);
      setSettings(data);

      const formValues: Record<string, string | boolean | number | null> = {};
      for (const cat of Object.values(data) as SettingItem[][]) {
        for (const s of cat) {
          if (s.type === 'boolean') {
            formValues[s.key] = s.value === 'true';
          } else if (s.type === 'number') {
            // Important: "0" is a valid value for hours/minutes.
            formValues[s.key] = s.value === null || s.value === '' ? null : Number(s.value);
          } else if (s.type === 'secret') {
            formValues[s.key] = ''; // never pre-fill secrets
          } else {
            formValues[s.key] = s.value ?? '';
          }
        }
      }
      form.setFieldsValue(formValues);
    } catch {
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    const values = form.getFieldsValue();
    const changes: { key: string; value?: string; type?: string; category?: string; description?: string }[] = [];

    for (const cat of categoryOrder) {
      const items = settings[cat] || [];
      for (const s of items) {
        const newVal = values[s.key];
        // A tab that was never opened has no fields mounted, so its settings
        // are missing from getFieldsValue(). Treating them as "" wiped every
        // other tab on save (10/14: SMTP, SMS, media and OAuth emptied on PROD).
        if (newVal === undefined) continue;
        // Secrets: only send if user typed a new value (field is always empty on load)
        if (s.type === 'secret') {
          if (!newVal) continue;
          changes.push({ key: s.key, value: newVal, type: s.type, category: s.category });
          continue;
        }
        const strVal = s.type === 'boolean' ? (newVal ? 'true' : 'false') : String(newVal ?? '');
        const currentVal = s.value ?? '';
        if (strVal !== currentVal) {
          changes.push({ key: s.key, value: strVal, type: s.type, category: s.category });
        }
      }
    }

    if (changes.length === 0) {
      message.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put(`${API_BASE_URL}/settings/batch`, { settings: changes });
      message.success(`${changes.length} setting(s) updated`);
      fetchSettings();
    } catch {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (s: SettingItem) => {
    if (s.type === 'boolean') {
      return <Form.Item name={s.key} valuePropName="checked" label={formatLabel(s.key)} extra={s.description}><Switch /></Form.Item>;
    }
    if (s.type === 'number') {
      const isHour = s.key.endsWith('_hour');
      const isMinute = s.key.endsWith('_minute');
      return (
        <Form.Item name={s.key} label={formatLabel(s.key)} extra={s.description}>
          <InputNumber
            style={{ width: '100%' }}
            min={isHour || isMinute ? 0 : undefined}
            max={isHour ? 23 : isMinute ? 59 : undefined}
          />
        </Form.Item>
      );
    }
    if (s.type === 'secret') {
      const hasValue = s.value?.startsWith('****');
      const hint = hasValue ? `Current: ${s.value} — leave blank to keep` : 'Not set — enter a value';
      return <Form.Item name={s.key} label={formatLabel(s.key)} extra={<span>{s.description && <span>{s.description} · </span>}<span style={{ color: hasValue ? '#52c41a' : '#999' }}>{hint}</span></span>}><Input.Password placeholder={hasValue ? 'Enter new value to replace' : 'Enter a value'} visibilityToggle={false} /></Form.Item>;
    }
    return <Form.Item name={s.key} label={formatLabel(s.key)} extra={s.description}><Input /></Form.Item>;
  };

  const tabItems = [
    ...categoryOrder
      .filter((cat) => settings[cat]?.length > 0)
      .map((cat) => ({
        key: cat,
        label: categoryLabels[cat] || cat,
        children: (
          <Card>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {settings[cat].map((s) => (
                <div key={s.key}>{renderField(s)}</div>
              ))}
            </Space>
          </Card>
        ),
      })),
    {
      key: 'cache',
      label: 'Cache',
      children: <ClearCacheTab />,
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>System Settings</h2>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
        <Form form={form} layout="vertical">
          <Tabs items={tabItems} />
        </Form>
      </div>
    </Spin>
  );
};

function formatLabel(key: string): string {
  return key
    .replace(/^(wellness|gamification)\./, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const CACHE_SCOPE_OPTIONS = CACHE_SCOPES;

/**
 * "Cache" tab (System Settings) — manual clearing of the shared reference-list
 * caches, per scope or all. Same endpoint as the dedicated Cache page
 * (POST /exercise-admin/cache/clear) — two entry points, one control.
 */
const ClearCacheTab: React.FC = () => {
  const [selected, setSelected] = useState<string[]>(
    CACHE_SCOPE_OPTIONS.map((s) => s.value),
  );
  const [clearing, setClearing] = useState(false);
  const [clearedAt, setClearedAt] = useState<string | null>(null);

  const handleClear = async () => {
    if (selected.length === 0 || clearing) return;
    setClearing(true);
    try {
      const { data } = await axiosInstance.post(
        `${API_BASE_URL}/exercise-admin/cache/clear`,
        { scopes: selected },
      );
      setClearedAt(new Date().toLocaleTimeString());
      message.success(`Caches cleared: ${(data?.cleared ?? selected).join(', ')}`);
    } catch {
      message.error('Failed to clear caches');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card>
      <Typography.Paragraph type="secondary">
        The API invalidates these caches automatically on CMS writes and via
        TTL. Use this after out-of-band changes (DB reseed, import scripts run
        outside the API process).
      </Typography.Paragraph>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {CACHE_SCOPE_OPTIONS.map((scope) => (
          <div key={scope.value}>
            <Checkbox
              checked={selected.includes(scope.value)}
              onChange={(e) =>
                setSelected((prev) =>
                  e.target.checked
                    ? [...prev, scope.value]
                    : prev.filter((v) => v !== scope.value),
                )
              }
            >
              <span style={{ fontWeight: 600 }}>{scope.label}</span>
              <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                {scope.description}
              </span>
            </Checkbox>
          </div>
        ))}
        <Button
          type="primary"
          danger
          icon={<ClearOutlined />}
          loading={clearing}
          disabled={selected.length === 0}
          onClick={handleClear}
        >
          Clear Selected ({selected.length})
        </Button>
        {clearedAt && (
          <span style={{ color: '#999', marginLeft: 12 }}>
            Last cleared at {clearedAt}
          </span>
        )}
      </Space>
    </Card>
  );
};
